/**
 * Виджет «Освоение по заказчикам (АБП)» (FR-7): мини-таблица
 * «заказчик · план · факт · % освоения (прогресс) · договоров»
 * с сортировкой по сумме — по плану / по факту (FR-7.2).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fmt } from './format';
import { SegToggle } from './SegToggle';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import type { AbpSlice } from '../../../types/projects';

type SortBy = 'plan' | 'fact';

interface AbpWidgetProps {
  abp: AbpSlice[];
}

export const AbpWidget = ({ abp }: AbpWidgetProps) => {
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState<SortBy>('plan');

  const rows = [...abp].sort((a, b) => {
    const va = (sortBy === 'plan' ? a.planMln : a.factMln) ?? 0;
    const vb = (sortBy === 'plan' ? b.planMln : b.factMln) ?? 0;
    return vb - va;
  });

  return (
    <Card className="shadow-card" data-testid="projects-abp">
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle>{t('projects.infrastructure.abp.title')}</CardTitle>
        <SegToggle<SortBy>
          ariaLabel={t('projects.infrastructure.abp.title')}
          options={[
            { value: 'plan', label: t('projects.infrastructure.abp.byPlan') },
            { value: 'fact', label: t('projects.infrastructure.abp.byFact') },
          ]}
          value={sortBy}
          onChange={setSortBy}
          testId="projects-abp-sort"
        />
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table className="min-w-[34rem]">
          <TableHeader>
            <TableRow>
              <TableHead>{t('projects.infrastructure.abp.customer')}</TableHead>
              <TableHead className="text-right">
                {t('projects.infrastructure.abp.plan')}
              </TableHead>
              <TableHead className="text-right">
                {t('projects.infrastructure.abp.fact')}
              </TableHead>
              <TableHead className="text-right">
                {t('projects.infrastructure.abp.development')}
              </TableHead>
              <TableHead className="text-right">
                {t('projects.infrastructure.abp.contracts')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const pct =
                row.planMln != null && row.planMln > 0 && row.factMln != null
                  ? (row.factMln / row.planMln) * 100
                  : null;
              return (
                <TableRow key={row.name} data-testid={`projects-abp-${row.name}`}>
                  <TableCell className="text-text-muted">{row.name}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.planMln != null ? fmt(row.planMln) : '—'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.factMln != null ? fmt(row.factMln) : '—'}
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center justify-end gap-2">
                      <span className="h-2 w-16 overflow-hidden rounded-full bg-surface-muted">
                        <span
                          className="block h-full rounded-full bg-brand-500"
                          style={{
                            width: `${Math.min(pct ?? 0, 100)}%`,
                          }}
                        />
                      </span>
                      <b className="w-9 text-right tabular-nums">
                        {pct != null ? `${Math.round(pct)}%` : '—'}
                      </b>
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.contractsCount != null ? fmt(row.contractsCount) : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
